/**
 * Search-param contract for the `/orca/repos/new` onboarding wizard.
 *
 * The wizard keeps its whole navigable position in the URL — step, repo slug,
 * mirror/direct mode, and the Linear team key — so a reload or a shared link
 * lands on the same frame (the same reasoning the clawinstall connect route uses
 * for its `?step=`, `packages/web/src/routes/clawinstall/onboarding/connect.tsx`).
 *
 * TanStack JSON-parses search values, so `?step=2` arrives as the NUMBER `2`;
 * `parseStep` handles both the number and the string form the same way
 * (mirrors `validateDesignBoardSearch` in `design-board.ts`).
 *
 * Pure and dependency-free: no router, no atoms, no RPC.
 *
 * @since 1.0.0
 */

/** The four onboarding steps, addressed by 1-based position. */
export type OnboardStep = 1 | 2 | 3 | 4;

/** How Orca reaches the source repository. The wizard never emits `"default"`. */
export type OnboardMode = "direct" | "mirror";

/**
 * The fully-resolved onboarding search contract. Every field is normalised:
 * `step` is clamped to 1..4, `repo` is present only when it is a valid slug,
 * `mode` defaults to `"direct"`, and `team` is upper-cased or absent.
 *
 * @since 1.0.0
 */
export interface OnboardSearch {
  readonly step: OnboardStep;
  /** `owner/name`, kept only when it matches {@link REPO_NAME_PATTERN}. */
  readonly repo: string | undefined;
  readonly mode: OnboardMode;
  /** Upper-cased Linear team key, absent when blank/non-string. */
  readonly team: string | undefined;
}

/**
 * GitHub `owner/name` slug shape. Re-declared here (the api service module
 * `packages/api/src/services/orca/repo-from-labels.ts:36` is not importable from
 * the web package) — keep the two in sync.
 */
export const REPO_NAME_PATTERN = /^[\w.-]+\/[\w.-]+$/;

/** Narrow an unknown to a wizard {@link OnboardMode}. */
export const isOnboardMode = (value: unknown): value is OnboardMode => value === "direct" || value === "mirror";

/** Clamp an unknown step value (number or numeric string) to 1..4, defaulting to 1. */
const parseStep = (value: unknown): OnboardStep => {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : Number.NaN;
  if (!Number.isFinite(n)) return 1;
  const t = Math.trunc(n);
  return t <= 1 ? 1 : t >= 4 ? 4 : t === 2 ? 2 : 3;
};

/**
 * Normalise the raw router search bag into the {@link OnboardSearch} contract.
 *
 * @since 1.0.0
 */
export const validateOnboardSearch = (search: Record<string, unknown>): OnboardSearch => {
  const repo = search["repo"];
  const team = search["team"];
  return {
    step: parseStep(search["step"]),
    repo: typeof repo === "string" && REPO_NAME_PATTERN.test(repo) ? repo : undefined,
    mode: isOnboardMode(search["mode"]) ? search["mode"] : "direct",
    team: typeof team === "string" && team.length > 0 ? team.toUpperCase() : undefined,
  };
};

/** Same repo/mode/team, a new step. */
export const stepSearch = (step: OnboardStep, search: OnboardSearch): OnboardSearch => ({ ...search, step });

/** Same step/repo/team, a new mode. */
export const withMode = (search: OnboardSearch, mode: OnboardMode): OnboardSearch => ({ ...search, mode });
